// The Pocket — timing reps against your own groove. The room plays the
// learner's beat keeper (the backbeat pattern before one exists) at the
// saved style lens's tempo (92 bpm before a lens is saved): four count-in
// clicks, then four bars, while the learner taps the beats on any input
// path — pointer piano, typing keys, or MIDI. Taps are captured as ms
// offsets from the transport start and handed to analyzeTiming; the read
// comes back as per-beat dots on a 16-slot strip (early left, late right),
// one warm feel line, and one actionable nudge. "again" restarts. No
// scores — the strip is a mirror, not a grade.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { PATCHES, getCtx, resumeAudio } from "../../../lib/homeKeyAudio";
import {
  DRUM_PATTERNS,
  STYLE_LENSES,
  startGroove,
  playHat,
} from "../../../lib/homeKeyGroove";
import { analyzeTiming, POCKET_FEEL_COPY } from "../../../lib/homeKeyPractice";
import Piano from "../Piano";
import useHomeKeyVoices from "../useHomeKeyVoices";
import useTypingKeys from "../useTypingKeys";
import useHomeKeyMidi from "../useHomeKeyMidi";

const BEATS = 16; // 4 bars × 4 beats
const LEAD_SEC = 0.2; // breath before the first count-in click

// One actionable nudge per feel — what to actually do next pass.
const FEEL_NUDGES = {
  "in the pocket":
    "keep it — try the next pass with your eyes closed and your shoulders down.",
  rushing:
    "you're arriving before the drums. Breathe out and let each click come to you.",
  dragging:
    "you're landing just behind the drums. Aim for the front edge of each click.",
  loose:
    "pick one anchor — the snare on 2 and 4 — and let the other beats hang off it.",
};

export default function PocketRoom({ keepers, onExit }) {
  // The groove is the learner's own beat when they've made one; the classic
  // backbeat stands in before then. Tempo comes from the saved style lens.
  const beatKeeper = keepers && keepers.beat && keepers.beat.pattern;
  const pattern = beatKeeper || DRUM_PATTERNS.backbeat;
  const lens =
    keepers && keepers.trackStyle && STYLE_LENSES[keepers.trackStyle.lensId];
  const bpm = lens ? lens.bpm : 92;
  const swing = lens ? lens.swing : 0.5;

  const [phase, setPhase] = useState("ready"); // ready | countin | playing | result
  const [countNum, setCountNum] = useState(0);
  const [beatNow, setBeatNow] = useState(null);
  const [tapCount, setTapCount] = useState(0);
  const [analysis, setAnalysis] = useState(null);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const runRef = useRef(null); // { startMs, taps }
  const grooveRef = useRef(null);
  const timersRef = useRef([]);

  const stopAll = useCallback(() => {
    if (grooveRef.current) grooveRef.current.stop();
    grooveRef.current = null;
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  const later = (fn, ms) => {
    timersRef.current.push(setTimeout(fn, ms));
  };

  const beatMs = 60000 / bpm;

  const finishRun = () => {
    stopAll();
    setBeatNow(null);
    const taps = (runRef.current && runRef.current.taps) || [];
    runRef.current = null;
    setAnalysis(analyzeTiming(taps, { bpm, beats: BEATS }));
    setPhase("result");
  };

  const start = () => {
    resumeAudio();
    stopAll();
    setAnalysis(null);
    setTapCount(0);
    setBeatNow(null);
    setCountNum(0);

    const countSec = 4 * (beatMs / 1000);
    const startDelayMs = (LEAD_SEC + countSec) * 1000;

    // Four count-in clicks on the audio clock, then the groove, started at
    // an absolute context time so the transport start is exact.
    for (let i = 0; i < 4; i += 1) {
      playHat(LEAD_SEC + (i * beatMs) / 1000);
      later(() => setCountNum(i + 1), LEAD_SEC * 1000 + i * beatMs);
    }
    const ctx = getCtx();
    grooveRef.current = startGroove({
      pattern,
      bpm,
      swing,
      bars: 4,
      startAtSec: ctx ? ctx.currentTime + LEAD_SEC + countSec : undefined,
      onStep: (step) => {
        if (step % 4 === 0) setBeatNow(step / 4);
      },
    });

    // Taps are ms offsets from the transport start (performance.now deltas).
    runRef.current = { startMs: performance.now() + startDelayMs, taps: [] };
    setPhase("countin");
    later(() => setPhase("playing"), startDelayMs);
    // Four bars, then read the taps (a small grace for the last beat's tail).
    later(finishRun, startDelayMs + BEATS * beatMs + 180);
  };

  // Any note-on, on any input path, is a tap. Slightly-early taps (inside
  // half a beat before the transport starts) count toward beat one.
  const recorder = {
    onNoteStart: (midi, tMs) => {
      const run = runRef.current;
      const ph = phaseRef.current;
      if (!run || (ph !== "countin" && ph !== "playing")) return;
      const delta = tMs - run.startMs;
      if (delta < -beatMs / 2) return; // still in the count-in
      run.taps.push(delta);
      setTapCount(run.taps.length);
    },
    onNoteEnd: () => {},
  };

  const voices = useHomeKeyVoices(PATCHES.softKeys, recorder);
  const { press, release, activeMidis } = voices;
  useTypingKeys(press, release, true);
  useHomeKeyMidi(press, release, true);

  // beat index → offsetMs, for the strip's dots.
  const perBeat = new Map(
    ((analysis && analysis.perBeat) || []).map((p) => [p.beat, p.offsetMs])
  );

  const strip = (
    <>
      <div className="hk-pocket-strip">
        {Array.from({ length: BEATS }, (_, b) => {
          const offset = perBeat.has(b) ? perBeat.get(b) : null;
          // A dot sits at the slot's centre when the tap was dead on; half a
          // beat of early/late spans the slot's width.
          const pos =
            offset == null
              ? null
              : 50 + Math.max(-1, Math.min(1, offset / (beatMs / 2))) * 45;
          const now = phase !== "result" && beatNow === b;
          return (
            <span
              key={b}
              className={`hk-pocket-slot${
                b % 4 === 0 ? " hk-pocket-slot--strong" : ""
              }${now ? " hk-pocket-slot--now" : ""}`}
            >
              {pos != null && (
                <span
                  className="hk-pocket-dot"
                  style={{ left: `${pos}%` }}
                  title={`${offset > 0 ? "+" : ""}${Math.round(offset)} ms`}
                />
              )}
            </span>
          );
        })}
      </div>
      <div className="hk-pocket-legend">
        <span>early</span>
        <span>late</span>
      </div>
    </>
  );

  return (
    <div className="hk-moment-body">
      {phase === "ready" && (
        <>
          <p>
            {beatKeeper
              ? "Your own beat, four bars of it."
              : "A classic backbeat, four bars of it."}{" "}
            Four clicks count you in, then tap along on the beats — any piano
            key, typing key, or MIDI note counts. Afterward the strip shows
            where each tap landed: left of centre is early, right is late.
          </p>
          <p className="hk-echo-hint">
            {bpm} bpm
            {lens ? ` — your track's “${lens.label}” tempo.` : " — an easy walking pace."}
          </p>
          <div className="hk-choice-row">
            <button
              type="button"
              className="hk-btn hk-btn--primary"
              onClick={start}
            >
              ▶ count me in
            </button>
          </div>
        </>
      )}

      {phase === "countin" && (
        <p className="hk-echo-status" aria-live="polite">
          {countNum > 0 ? `${countNum}…` : "…"} (four clicks, then you're on)
        </p>
      )}

      {phase === "playing" && (
        <p className="hk-echo-status" aria-live="polite">
          tap the beats — right with the drums.
          {tapCount > 0 && ` ${tapCount} so far…`}
        </p>
      )}

      {(phase === "countin" || phase === "playing") && strip}

      {phase === "result" && analysis && (
        <>
          {strip}
          <p className="hk-echo-status">
            {POCKET_FEEL_COPY[analysis.feel] || analysis.feel}
            {` You caught ${analysis.hits} of the ${BEATS} beats.`}
          </p>
          <p className="hk-echo-hint">{FEEL_NUDGES[analysis.feel]}</p>
          <div className="hk-choice-row">
            <button
              type="button"
              className="hk-btn hk-btn--primary"
              onClick={start}
            >
              again
            </button>
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={onExit}
            >
              back to the hall
            </button>
          </div>
        </>
      )}

      <Piano
        activeMidis={activeMidis}
        onPress={press}
        onRelease={release}
      />
    </div>
  );
}
